/**
 * Accounting-format money helpers. Source of truth for every dollar
 * figure rendered in the accounting UI: thousands separators, two
 * decimal places, parentheses for negatives (credit / owing direction
 * depends on context — see the `<Money>` component for color coding).
 *
 *   1088.09     -> "$1,088.09"
 *   -257.79     -> "($257.79)"
 *   0           -> "$0.00"
 *   1000000     -> "$10,000.00"    (100000000 cents)
 */

const BASE_FORMATTER = new Intl.NumberFormat('en-US', {
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
});

/**
 * Format an integer-cents value as accounting-style USD. Returns the
 * string only — render via `<Money>` to also get color coding.
 */
export function formatMoneyCents(cents: number | null | undefined, options: {
	/** Use parentheses for negatives instead of a minus sign. Default true. */
	accounting?: boolean;
} = {}): string {
	const c = Number(cents ?? 0);
	const abs = Math.abs(c) / 100;
	const formatted = BASE_FORMATTER.format(abs);
	const accounting = options.accounting !== false;
	if (c < 0) return accounting ? `($${formatted})` : `-$${formatted}`;
	return `$${formatted}`;
}

/**
 * Format a dollar-decimal number (NOT cents) the same way — for inputs
 * where the source is already in dollars (e.g. QB CSV amounts).
 */
export function formatMoneyDollars(dollars: number | null | undefined, options: { accounting?: boolean } = {}): string {
	return formatMoneyCents(Math.round(Number(dollars ?? 0) * 100), options);
}

/**
 * Reinterpret an arbitrary text input as a dollar string built from
 * cents-as-typed digits. Strips every non-digit (and `(...)`/`-` for sign),
 * then re-inserts the decimal point two places from the right.
 *
 *   "14119"   -> "141.19"
 *   "9"       -> "0.09"
 *   ""        -> ""
 *   "141.19"  -> "141.19"  (paste — decimal stripped, then re-inserted)
 *   "(50)"    -> "-0.50"
 *
 * Used by `<MoneyTextInput>` so that decimal entry is implicit. Always
 * yields a string compatible with `parseMoneyToCents`.
 */
export function reinterpretAsCents(incoming: string | null | undefined): string {
	if (!incoming) return '';
	const trimmed = incoming.trim();
	const negative = trimmed.startsWith('-') || (trimmed.startsWith('(') && trimmed.endsWith(')'));
	const digits = incoming.replace(/\D/g, '');
	if (!digits) return negative ? '-' : '';
	const padded = digits.padStart(3, '0');
	const intPart = padded.slice(0, -2).replace(/^0+(?=\d)/, '') || '0';
	const decPart = padded.slice(-2);
	return (negative ? '-' : '') + intPart + '.' + decPart;
}

/**
 * Parse a user-entered money string ("$1,234.56", "-5", "1234", "(50.00)")
 * to integer cents. Returns NaN if unparseable. Strips $, commas,
 * whitespace; treats parenthesized values as negative.
 */
export function parseMoneyToCents(raw: string): number {
	if (!raw) return 0;
	let s = raw.trim();
	let negative = false;
	if (s.startsWith('(') && s.endsWith(')')) {
		negative = true;
		s = s.slice(1, -1);
	}
	s = s.replace(/[$,\s]/g, '');
	if (s.startsWith('-')) { negative = true; s = s.slice(1); }
	const n = Number(s);
	if (!Number.isFinite(n)) return NaN;
	return Math.round(n * 100) * (negative ? -1 : 1);
}
